import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { HardHat, Briefcase, CheckCircle2, Clock, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";

const ACTIVE = ["scheduled", "en_route", "in_progress", "needs_review"];

export default function SubDashboard() {
  const { user } = useAuth();
  const { data: jobs, isLoading } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });
  if (isLoading) return <div className="p-10"><Spinner /></div>;

  const active = (jobs || []).filter((j) => ACTIVE.includes(j.status));
  const done = (jobs || []).filter((j) => j.status === "completed");

  const Stat = ({ icon: Icon, label, value, tone }) => (
    <div className="card-tac p-5">
      <div className={`grid h-10 w-10 place-items-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-display text-3xl font-black text-white">{value}</div>
      <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="sub-dashboard">
      <Eyebrow>Subcontractor portal</Eyebrow>
      <h1 className="mt-2 font-display text-3xl font-bold text-white">My work</h1>
      <p className="mt-2 text-sm text-slate-400">Welcome back, {user?.name}. You only see the jobs you're assigned to.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Briefcase} label="Assigned jobs" value={jobs.length} tone="bg-amber-500/10 text-amber-400" />
        <Stat icon={Clock} label="Active now" value={active.length} tone="bg-cyan-500/10 text-cyan-400" />
        <Stat icon={CheckCircle2} label="Completed" value={done.length} tone="bg-emerald-500/10 text-emerald-400" />
        <Stat icon={DollarSign} label="Pending payout" tone="bg-emerald-500/10 text-emerald-400"
          value={`$${(jobs || []).reduce((s, j) => s + (j.payout && j.payout.status !== "paid" ? (j.payout.amount || 0) : 0), 0).toLocaleString()}`} />
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold text-white">Active assignments</h2>
      <div className="mt-3 space-y-3">
        {active.map((j) => (
          <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`sub-job-${j.id}`} className="card-tac flex items-center justify-between p-4 transition-colors hover:border-amber-500/40">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-800 text-slate-300"><HardHat className="h-5 w-5" /></div>
              <div>
                <div className="text-sm font-semibold text-white">{j.title}</div>
                <div className="text-xs text-slate-400">{j.customer_name} · {j.property_address}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {j.payout && <span data-testid={`sub-payout-${j.id}`} className={`font-mono-tac text-xs ${j.payout.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{j.payout.status === "paid" ? "Paid" : "Due"} ${Number(j.payout.amount || 0).toLocaleString()}</span>}
              <StatusPill status={j.status} />
            </div>
          </Link>
        ))}
        {!active.length && <div className="card-tac py-10 text-center text-slate-500" data-testid="sub-no-active">No active assignments right now.</div>}
      </div>
    </div>
  );
}
